import { AccountCertification } from '@wix/ambassador-academy-certifications-v1-account-certification/types';
import { Box, Carousel } from '@wix/design-system';
import React, { useState, useRef, useEffect } from 'react';
import LabeledCertification from './CertificationDisplay/LabeledCertification';
import CarouselNavigationButtons from './CarouselNavigationButtons';
import { useProfilePageBi } from '@components/Hooks/useProfilePageBi';
import s from './Certifications.module.scss';

const CAROUSEL_NAVIGATION_BI_EVENT = 'Navigating between certifications';

interface CertificationsCarouselProps {
  certifications: AccountCertification[];
  isMobileView?: boolean;
  maxSlideIndex: number;
  onGradientStateChange?: (showLeft: boolean, showRight: boolean) => void;
}

const CertificationsCarousel: React.FC<CertificationsCarouselProps> = ({
  certifications,
  isMobileView,
  maxSlideIndex,
  onGradientStateChange,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef<any>(null);
  const { logProfilePageClick } = useProfilePageBi();

  const handlePrevious = () => {
    if (carouselRef.current && currentSlide > 0) {
      carouselRef.current.slideTo(currentSlide - 1);
      logProfilePageClick(CAROUSEL_NAVIGATION_BI_EVENT);
    }
  };

  const handleNext = () => {
    if (carouselRef.current && currentSlide < maxSlideIndex) {
      carouselRef.current.slideTo(currentSlide + 1);
      logProfilePageClick(CAROUSEL_NAVIGATION_BI_EVENT);
    }
  };

  const showPrevButton = currentSlide > 0;
  const showNextButton = currentSlide < maxSlideIndex;

  useEffect(() => {
    if (onGradientStateChange) {
      onGradientStateChange(showPrevButton, showNextButton);
    }
  }, [showPrevButton, showNextButton, onGradientStateChange]);

  return (
    <div className={s.certificationsCarousel}>
      <Carousel
        ref={carouselRef}
        controlsPosition="none"
        dots={false}
        variableWidth={true}
        infinite={false}
        afterChange={(index) => setCurrentSlide(index)}
        dataHook="certifications-carousel"
      >
        {certifications.map((cert, index) => (
          <Box
            key={cert.certificationType}
            marginRight={index === certifications.length - 1 ? '0' : '24px'}
          >
            <LabeledCertification cert={cert} isMobile={isMobileView} />
          </Box>
        ))}
      </Carousel>

      <CarouselNavigationButtons
        showPrevButton={showPrevButton}
        showNextButton={showNextButton}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />
    </div>
  );
};

export default CertificationsCarousel;
